// Per spec/blocks.md §7. Standalone section heading used before multi-block
// groupings (e.g., "Upcoming Chapter Events" introduces three event-card
// sub-section blocks below it). Composes the SectionHeader Component.
//
// `RenderContext.followingBlocksAllHidden` powers the auto-hide: if every
// subsequent block in the email is skipped (`included: false` or
// `autoHide() === true`), the heading would dangle above empty space, so
// it returns an empty HtmlString instead. `renderEmail` (P-019) computes
// the field; this Block is the first consumer.
//
// Empty short-circuit uses the shared `EMPTY_HTML` sentinel from `../types`
// (D-080). Empty string carries no XSS surface — one of the documented
// exceptions to the "construct HtmlString only inside html.ts/sanitize.ts"
// rule (D-021). Hoisted at the third use; section-heading was the first
// consumer (D-052), render-block.ts the second (D-077), render-email.ts
// the third (P-019).
//
// **P-037.8 (D-108): CTA fields are independently optional.** Banks's PSD
// audit relaxed the prior D-033 paired-optional pattern for this Block
// only — `ctaLabel` and `ctaUrl` are each `z.string().min(1).optional()`
// / `z.string().url().optional()`. The render forwards an atomic CTA to
// the Component only when both values are present; either alone falls
// through to the no-CTA arm. Other consumers of `SectionHeader`
// (day-chairs, chapter-sponsors, etc.) still pass the paired-required
// `SectionHeaderProps` union at the Component layer.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { sectionHeader } from '../components/section-header'
import { EMPTY_HTML } from '../types'

// **P-037.9 (D-109): `size` and `ctaStyle` options dropped.** Banks's audit:
// `size` carried no visual variation (both arms rendered at 21 px since
// P-037.8); sub-section content moved to the dedicated `sub-section-heading`
// Block instead. `ctaStyle` dropped — chapter only uses link-style CTAs.
const optionsSchema = z.object({})

const inputsSchema = z.object({
  heading: z.string().min(1),
  ctaLabel: z.string().min(1).optional(),
  ctaUrl: z.string().url().optional(),
})

export const sectionHeading = defineBlock({
  id: 'section-heading',
  label: 'Section Heading',
  category: 'meta',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: { heading: 'Upcoming Chapter Events' },
  render: ({ inputs }, ctx) => {
    if (ctx.followingBlocksAllHidden) return EMPTY_HTML
    // P-037.18 D-118: thread ctx.linkColor through so the CTA arm renders
    // in the template-level link color when both ctaLabel + ctaUrl are set.
    // P-037.19 D-125: thread ctx.subheaderImageUrl so the SectionHeader
    // Component renders the per-Header-style decorative banner above the
    // heading. `section-heading` is the only consumer that forwards this;
    // other SectionHeader callers (day-chairs, chapter-sponsors, …) leave
    // the prop unset and render without a banner.
    if (inputs.ctaLabel === undefined || inputs.ctaUrl === undefined) {
      return sectionHeader({
        heading: inputs.heading,
        linkColor: ctx.linkColor,
        subheaderImageUrl: ctx.subheaderImageUrl,
      })
    }
    return sectionHeader({
      heading: inputs.heading,
      ctaLabel: inputs.ctaLabel,
      ctaUrl: inputs.ctaUrl,
      linkColor: ctx.linkColor,
      subheaderImageUrl: ctx.subheaderImageUrl,
    })
  },
})
